import { createFileRoute } from "@tanstack/react-router";
import { PageWrapper } from "@/components/PageWrapper.tsx";
import { Loader } from "@/components/Loader.tsx";
import { FetchingError } from "@/components/FetchingError.tsx";
import { t } from "@lingui/core/macro";
import { PageHeader } from "@/components/PageHeader.tsx";
import { AllBankDataProvider, useBankData } from "@/dataProviders/BankDataProvider.tsx";
import { NOT_AVAILABLE } from "@/lib/utils.ts";
import { getLocation } from "@/lib/table.ts";
import { FOREIGN_LOCATION_KEY } from "@/routes/payments/foreign/index.tsx";
import { ForeignTransactionDetail } from "@/modules/foreignPayments/ForeignTransactionDetail.tsx";
import { useForeignTransactionQuery } from "@/modules/foreignPayments/api/useForeignTransactions.ts";
import { formatPostalAddress } from "@/modules/foreignPayments/utils.ts";

export const Route = createFileRoute("/payments/foreign/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <AllBankDataProvider>
      <ForeignDetailContent />
    </AllBankDataProvider>
  );
}

function ForeignDetailContent() {
  const { id } = Route.useParams();
  const title = t`Foreign payments detail`;
  const { bankData } = useBankData();
  const { data, isError, isPending, error } = useForeignTransactionQuery(
    {
      merchantTransactionId: id,
    },
    { enabled: !!bankData },
  );

  const logoUrl = bankData[data?.paymentProvider ?? ""]?.logoUrl;

  return (
    <PageWrapper>
      {isError && (
        <FetchingError
          message={t`Something went wrong. We were unable to load the data. Please refresh the page and try again.`}
          error={error}
        />
      )}
      {isPending && <Loader message={t`Loading foreign payment...`} />}
      {data && (
        <>
          <PageHeader
            breadcrumbs={[
              { href: `/payments/foreign/${getLocation(FOREIGN_LOCATION_KEY)}`, title: t`Foreign payments` },
              { href: "/", title },
            ]}
          />
          {bankData && (
            <ForeignTransactionDetail
              amount={data.amount ?? 0}
              application={data.applicationName ?? ""}
              bankAuthId={data.bankAuthId ?? ""}
              bankPaymentId={data.bankPaymentId ?? ""}
              callbackUrl={data.callbackUrl ?? ""}
              chargeBearer={data.chargeBearer ?? NOT_AVAILABLE}
              clientId={data.clientId ?? ""}
              createdAt={data.createdAt}
              creditorAccountCurrency={data.creditorAccountIdentification?.currency ?? NOT_AVAILABLE}
              creditorAccountType={data.creditorAccountIdentification?.type ?? NOT_AVAILABLE}
              creditorAccountValue={data.creditorAccountIdentification?.value ?? ""}
              creditorAgentBic={data.creditorAgent?.bic ?? NOT_AVAILABLE}
              creditorAgentName={data.creditorAgent?.name ?? NOT_AVAILABLE}
              creditorAgentAddress={formatPostalAddress(data.creditorAgent?.postalAddress) || NOT_AVAILABLE}
              creditorName={data.creditorName ?? ""}
              creditorPostalAddress={formatPostalAddress(data.creditorPostalAddress) || NOT_AVAILABLE}
              currency={data.currency ?? ""}
              customerId={data.merchantTransactionId ?? ""}
              debtorAccountCurrency={data.debtorAccountIdentification?.currency ?? NOT_AVAILABLE}
              debtorAccountType={data.debtorAccountIdentification?.type ?? NOT_AVAILABLE}
              debtorAccountValue={data.debtorAccountIdentification?.value ?? ""}
              debtorName={data.debtorName ?? ""}
              description={data.description ?? ""}
              flowId={data.frontendFlowId ?? ""}
              initiator={data.initiatorName ?? ""}
              isFinalBankStatus={data.isFinalBankStatus ?? false}
              logoUrl={logoUrl}
              merchantId={data.merchantId ?? ""}
              operationId={data.operationId ?? ""}
              paymentProvider={data.paymentProvider ?? ""}
              priority={data.instructionPriority ?? ""}
              transactionStatus={data.resultCode ?? "OPENED"}
              updatedAt={data.updatedAt}
              autoRejected={data.autoRejected ?? false}
              bankInstructionStatus={data.bankInstructionStatus ?? NOT_AVAILABLE}
            />
          )}
        </>
      )}
    </PageWrapper>
  );
}
